import { Component } from '@theme/component';

/**
 * @typedef {Object} WomenliBlogRefs
 * @property {HTMLElement} [grid]
 * @property {HTMLElement} [featured]
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} [showMoreWrap]
 * @property {HTMLButtonElement} [showMore]
 * @property {HTMLButtonElement[]} [filterButtons]
 */

/**
 * Blog index with category filtering and show-more pagination.
 *
 * @extends {Component<WomenliBlogRefs>}
 */
class WomenliBlog extends Component {
  /** @type {string} */
  #activeFilter = 'all';

  /** @type {boolean} */
  #expanded = false;

  connectedCallback() {
    super.connectedCallback();
    this.#bindEvents();
    this.#applyFilter();
  }

  #bindEvents() {
    this.refs.filterButtons?.forEach((button) => {
      button.addEventListener('click', this.#onFilterClick);
    });

    this.refs.showMore?.addEventListener('click', this.#onShowMore);
  }

  /**
   * @param {Event} event
   */
  #onFilterClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const filter = button.dataset.filter;
    if (!filter) return;

    this.#activeFilter = filter;
    this.#expanded = false;

    this.refs.filterButtons?.forEach((item) => {
      item.classList.toggle('is-active', item === button);
    });

    this.#applyFilter();
  };

  #onShowMore = () => {
    this.#expanded = true;
    this.#applyFilter();
  };

  #applyFilter() {
    const { grid, featured, empty, showMoreWrap } = this.refs;
    if (!grid) return;

    const postsPerPage = Number(this.dataset.postsPerPage) || 9;
    const cards = grid.querySelectorAll('[data-card]');
    let visibleCount = 0;
    let shownCount = 0;
    let hasHiddenByLimit = false;

    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      const tags = (card.dataset.tags || '').split(/\s+/).filter(Boolean);
      const matches = this.#activeFilter === 'all' || tags.includes(this.#activeFilter);

      card.hidden = !matches;
      card.classList.toggle('is-hidden', !matches);

      if (!matches) return;

      visibleCount += 1;

      const shouldHideByLimit = !this.#expanded && shownCount >= postsPerPage;
      if (shouldHideByLimit) {
        card.hidden = true;
        card.classList.add('is-hidden');
        hasHiddenByLimit = true;
      } else {
        card.hidden = false;
        card.classList.remove('is-hidden');
        shownCount += 1;
      }
    });

    if (featured instanceof HTMLElement) {
      const featuredTags = (featured.dataset.tags || '').split(/\s+/).filter(Boolean);
      const featuredMatches = this.#activeFilter === 'all' || featuredTags.includes(this.#activeFilter);
      featured.classList.toggle('is-filtered-hidden', !featuredMatches);
    }

    if (empty instanceof HTMLElement) {
      empty.hidden = visibleCount > 0;
    }

    if (showMoreWrap instanceof HTMLElement) {
      showMoreWrap.classList.toggle('is-hidden', !hasHiddenByLimit || visibleCount === 0);
    }
  }
}

if (!customElements.get('womenli-blog')) {
  customElements.define('womenli-blog', WomenliBlog);
}
